import React, { useState } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import {
    Avatar,
    Box,
    Typography,
    IconButton,
    Paper,
    Stack,
    Menu,
    MenuItem,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectPersonId } from "@/auth/authSlice";
import {
    formatDateStringToDayMonth,
    formatDateStringToDayMonthYear,
    formatDateToDetailed,
} from "@/utils";
import { CommentInputWithPersonTagging } from "./Comments";
import { CommentInput } from "./CommentThread";

const Comment = ({
    personName = "Anonymous",
    personId,
    iconUrl,
    timePosted,
    body,
    onResolve,
    onReply,
    onEdit,
    onDelete,
    resolved,
    children,
    canReply,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [editing, setEditing] = useState(false);
    const [commentEditInput, setCommentEditInput] = useState(body);
    const open = Boolean(anchorEl);
    const handleMenuOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleBeginEdit = () => {
        setEditing(true);
        setAnchorEl(null);
        setCommentEditInput(body);
    };
    const handleEdit = (editContent) => {
        onEdit(editContent);
        setEditing(false);
    };
    const handleCancelEdit = () => {
        setEditing(false);
        setCommentEditInput(body);
    };
    const handleResolve = (e) => {
        e.stopPropagation();
        onResolve();
    };
    const reduxPersonId = useSelector(selectPersonId);
    const isMyComment = personId == reduxPersonId;
    return (
        <div>
            <Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box display="flex" alignItems="center">
                        <Avatar
                            src={iconUrl}
                            alt={personName}
                            sx={{ width: 22, height: 22, mr: 1.5 }}
                        />
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ lineHeight: 1.1 }}
                            >
                                {personName}
                            </Typography>
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="caption" sx={{ fontSize: 13 }}>
                            {formatDateToDetailed(timePosted)}
                        </Typography>
                    </Box>
                </Box>
                {editing ? (
                    <div style={{ marginTop: 15 }}>
                        <CommentInput
                            confirmButtonName="Save"
                            onCommentInputConfirm={handleEdit}
                            onCancel={handleCancelEdit}
                            commentInputInitialValue={commentEditInput}
                        />
                    </div>
                ) : (
                    <>
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1.2,
                                wordBreak: "break-word",
                                fontSize: 16,
                                paddingLeft: 0.5,
                            }}
                            component="div"
                        >
                            {body}
                        </Typography>
                        <Box pt={1} pb={1.5}>
                            {isMyComment && (
                                <IconButton
                                    size="small"
                                    onClick={handleResolve}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        overflow: "visible",
                                    }}
                                >
                                    <CheckRoundedIcon sx={{ fontSize: 24 }} />
                                </IconButton>
                            )}
                            {isMyComment && (
                                <IconButton
                                    size="small"
                                    onClick={handleMenuOpen}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        overflow: "visible",
                                    }}
                                >
                                    <MoreVertRoundedIcon
                                        sx={{ fontSize: 20 }}
                                    />
                                </IconButton>
                            )}
                        </Box>
                    </>
                )}
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    slotProps={{
                        list: {
                            "aria-labelledby": "basic-button",
                        },
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <MenuItem onClick={handleBeginEdit}>Edit</MenuItem>
                    <MenuItem onClick={onResolve}>Resolve</MenuItem>
                </Menu>
                {children}
            </Box>
        </div>
    );
};

export default Comment;
